import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  Avatar, Stack, CircularProgress, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Tooltip,
} from '@mui/material';
import { ArrowLeft2, People, TaskSquare, Warning2, TickCircle, Add, Trash, ArrowRight2 } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { useTeamStore } from '../../store/team.store';
import { useEmployeeStore } from '../../store/employee.store';
import { useAuthStore } from '../../store/auth.store';
import { Task } from '../../types/task.type';
import { isOverdue, getWorkStatus, normalizeTask, sortTasks, WORK_STATUS_COLORS, workStatusLabelKey } from '../../utils/task.utils';
import { getTeamEmployees, getTeamTasks, addEmployeeToTeam, removeEmployeeFromTeam } from '../../services/team.service';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import TeamSettingsDrawer, { TeamSettingsTrigger } from '../../components/teams/TeamSettingsDrawer';
import TaskTableSkeleton from '../../components/tasks/TaskTableSkeleton';
import TasksRefreshButton from '../../components/tasks/TasksRefreshButton';

interface TeamMemberLocal { id: string; name: string; email: string; }

export default function TeamDetailsPage() {
  const { teamId }  = useParams<{ teamId: string }>();
  const navigate    = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();

  const { teams, isLoading: teamsLoading, fetchTeams } = useTeamStore();
  const { employees, fetchEmployees } = useEmployeeStore();
  const { user } = useAuthStore();

  const [members, setMembers] = useState<TeamMemberLocal[]>([]);
  const [teamTasks, setTeamTasks] = useState<Task[]>([]);
  const [memLoading, setMemLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksRefreshing, setTasksRefreshing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addEmpId, setAddEmpId] = useState('');
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadMembers = useCallback(() => {
    if (!teamId) return;
    setMemLoading(true);
    getTeamEmployees(teamId)
      .then((res: any) => setMembers(Array.isArray(res) ? res : (res.data ?? [])))
      .catch(() => setMembers([]))
      .finally(() => setMemLoading(false));
  }, [teamId]);

  const loadTeamTasks = useCallback(() => {
    if (!teamId) return;
    setTeamTasks([]);
    setTasksLoading(true);
    getTeamTasks(teamId)
      .then((res: any) => {
        const data = (res.data ?? []).map(normalizeTask);
        setTeamTasks(data);
      })
      .catch(() => setTeamTasks([]))
      .finally(() => setTasksLoading(false));
  }, [teamId]);

  const handleRefreshTasks = () => {
    if (!teamId) return;
    setTasksRefreshing(true);
    getTeamTasks(teamId)
      .then((res: any) => {
        const data = (res.data ?? []).map(normalizeTask);
        setTeamTasks(data);
      })
      .finally(() => setTasksRefreshing(false));
  };

  useEffect(() => {
    fetchTeams();
    fetchEmployees();
  }, [fetchTeams, fetchEmployees]);

  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadTeamTasks(); }, [loadTeamTasks]);

  const team = teams.find(t => t.id === teamId);

  if (teamsLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  if (!team) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography sx={{ color: 'text.secondary' }}>{t('teams.notFound')}</Typography>
        <Button onClick={() => navigate('/dashboard/teams')} sx={{ mt: 2 }}>{t('teams.backToTeams')}</Button>
      </Box>
    );
  }

  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const sortedTasks = sortTasks(teamTasks, 'due', 'asc');
  const overdueTasks   = teamTasks.filter(t => isOverdue(t)).length;
  const activeTasks    = teamTasks.filter(t => !isOverdue(t)).length;

  const approvedEmployees = employees.filter(e => e.google_access_status === 'approved');
  const memberIds = new Set(members.map(m => m.id));
  const availableToAdd = approvedEmployees.filter(e => !memberIds.has(e.id));

  const handleAddMember = async () => {
    if (!addEmpId || !teamId) return;
    setSaving(true);
    try {
      await addEmployeeToTeam(teamId, addEmpId);
      const emp = employees.find(e => e.id === addEmpId);
      if (emp) setMembers(prev => [...prev, { id: emp.id, name: emp.name, email: emp.email }]);
      setAddEmpId('');
      setAddOpen(false);
      loadTeamTasks();
      showToast(t('teams.memberAdded'), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (empId: string) => {
    if (!teamId) return;
    try {
      await removeEmployeeFromTeam(teamId, empId);
      setMembers(prev => prev.filter(m => m.id !== empId));
      loadTeamTasks();
      showToast(t('teams.memberRemoved'), TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    }
  };

  const handleSettingsUpdated = () => {
    loadTeamTasks();
    fetchTeams();
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowLeft2 size={16} color="#6B7280" />}
          onClick={() => navigate('/dashboard/teams')}
          sx={{ color: 'text.secondary', mb: 2, fontWeight: 400, fontSize: '0.875rem' }}
        >
          {t('teams.backToTeams')}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: `${team.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${team.color}30` }}>
              <People size={24} color={team.color} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.3px' }}>{team.name}</Typography>
              {team.description && (
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{team.description}</Typography>
              )}
            </Box>
          </Box>
          <TeamSettingsTrigger team={team} onClick={() => setSettingsOpen(true)} />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('tasks.statuses.needsAction'), value: activeTasks,    icon: <TaskSquare size={18} color="#3B82F6" />, color: '#3B82F6', bg: '#EFF6FF' },
          { label: t('tasks.statuses.overdue'),     value: overdueTasks,   icon: <Warning2  size={18} color="#EF4444" />, color: '#EF4444', bg: '#FEF2F2' },
          { label: t('dashboard.totalTasks'),       value: teamTasks.length, icon: <TickCircle size={18} color="#10B981" />, color: '#10B981', bg: '#F0FDF4' },
          { label: t('teams.members'),               value: members.length + (team.include_manager_tasks ? 1 : 0), icon: <People size={18} color="#8B5CF6" />, color: '#8B5CF6', bg: '#F5F3FF' },
        ].map(stat => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>{stat.label}</Typography>
                  {stat.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{stat.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5} alignItems="stretch">
        <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                  {t('teams.members')} ({members.length})
                </Typography>
                <Button
                  size="small" variant="outlined"
                  startIcon={<Add size={14} color="#2563EB" />}
                  onClick={() => setAddOpen(true)}
                  disabled={availableToAdd.length === 0}
                  sx={{ fontSize: '0.8125rem' }}
                >
                  {t('teams.addMember')}
                </Button>
              </Box>

              {team.include_manager_tasks && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 1, py: 1, borderRadius: 2, bgcolor: '#F0F9FF', border: '1px solid #BFDBFE' }}>
                  <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg, #8B5CF6, #6366F1)', fontWeight: 600, fontSize: '0.875rem' }}>
                    {(user?.name ?? t('teams.spaceFromManager')).charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{user?.name ?? t('teams.spaceFromManager')}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{t('teams.includeMyTasks')}</Typography>
                  </Box>
                </Box>
              )}

              {memLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : members.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', textAlign: 'center', py: 3 }}>
                  {t('teams.noMembers')}
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {members.map(emp => (
                    <Box key={emp.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg, #3B82F6, #4F46E5)', fontWeight: 600, fontSize: '0.875rem', flexShrink: 0 }}>
                        {emp.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.name}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{emp.email}</Typography>
                      </Box>
                      <Tooltip title={t('teams.removeMember')}>
                        <Box
                          onClick={() => handleRemoveMember(emp.id)}
                          sx={{ cursor: 'pointer', color: '#EF4444', opacity: 0.6, '&:hover': { opacity: 1 }, flexShrink: 0 }}
                        >
                          <Trash size={15} color="#EF4444" />
                        </Box>
                      </Tooltip>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                  {t('layout.tasks')} ({teamTasks.length})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TasksRefreshButton onClick={handleRefreshTasks} refreshing={tasksRefreshing} />
                  <Button
                    component={RouterLink}
                    to={`/dashboard/tasks?team=${teamId}`}
                    size="small"
                    endIcon={<ArrowRight2 size={14} />}
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    {t('teams.viewAllTasks')}
                  </Button>
                </Box>
              </Box>

              {tasksLoading ? (
                <TaskTableSkeleton variant="compact" rows={6} />
              ) : sortedTasks.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{t('tasks.noTasks')}</Typography>
                </Box>
              ) : (
                sortedTasks.slice(0, 8).map((task, i) => {
                  const workStatus = getWorkStatus(task);
                  const cfg = WORK_STATUS_COLORS[workStatus];
                  return (
                    <Box
                      key={`${task.employee_id}:${task.task_list_id}:${task.google_task_id}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: i < Math.min(sortedTasks.length, 8) - 1 ? '1px solid #F1F5F9' : 'none' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {task.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {task.employee_name}
                          {task.space_name && ` · ${task.space_name}`}
                          {task.due && ` · ${new Date(task.due).toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`}
                        </Typography>
                      </Box>
                      <Chip label={t(workStatusLabelKey(workStatus))} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 500, fontSize: '0.75rem', border: 'none', height: 24 }} />
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TeamSettingsDrawer
        team={team}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onUpdated={handleSettingsUpdated}
      />

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('teams.addMember')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>{t('members.title')}</InputLabel>
            <Select value={addEmpId} label={t('members.title')} onChange={e => setAddEmpId(e.target.value as string)}>
              {availableToAdd.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.name} — {e.email}</MenuItem>
              ))}
              {availableToAdd.length === 0 && (
                <MenuItem disabled>{t('teams.noAvailableEmployees')}</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAddMember} disabled={!addEmpId || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Add size={16} color="#fff" />}>
            {t('teams.addMember')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
